import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { Pesée } from '@/types/weighing';
import weighingService from '@/services/weighing';

// Types pour le contexte
export interface WeighingState {
  currentWeighing: Pesée | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export interface WeighingContextType {
  state: WeighingState;
  // Actions
  setCurrentWeighing: (weighing: Pesée | null) => void;
  updateWeighing: (updates: Partial<Pesée>) => void;
  refreshWeighing: (weighingId: string) => Promise<void>;
  clearWeighing: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// Actions pour le reducer
type WeighingAction =
  | { type: 'SET_CURRENT_WEIGHING'; payload: Pesée | null }
  | { type: 'UPDATE_WEIGHING'; payload: Partial<Pesée> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_WEIGHING' }
  | { type: 'SET_LAST_UPDATED'; payload: Date };

// État initial
const initialState: WeighingState = {
  currentWeighing: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

// Reducer
function weighingReducer(state: WeighingState, action: WeighingAction): WeighingState {
  switch (action.type) {
    case 'SET_CURRENT_WEIGHING':
      return {
        ...state,
        currentWeighing: action.payload,
        lastUpdated: new Date(),
        error: null,
      };
    
    case 'UPDATE_WEIGHING':
      return {
        ...state,
        currentWeighing: state.currentWeighing 
          ? { ...state.currentWeighing, ...action.payload }
          : null,
        lastUpdated: new Date(),
        error: null,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    
    case 'CLEAR_WEIGHING':
      return {
        ...initialState,
      };
    
    case 'SET_LAST_UPDATED':
      return {
        ...state,
        lastUpdated: action.payload,
      };
    
    default:
      return state;
  }
}

// Création du contexte
const WeighingContext = createContext<WeighingContextType | undefined>(undefined);

// Provider du contexte
export const WeighingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(weighingReducer, initialState);

  // Action pour définir la pesée actuelle
  const setCurrentWeighing = useCallback((weighing: Pesée | null) => {
    dispatch({ type: 'SET_CURRENT_WEIGHING', payload: weighing });
  }, []);

  // Action pour mettre à jour la pesée actuelle
  const updateWeighing = useCallback((updates: Partial<Pesée>) => {
    dispatch({ type: 'UPDATE_WEIGHING', payload: updates });
  }, []);

  // Action pour recharger les données de la pesée depuis l'API
  const refreshWeighing = useCallback(async (weighingId: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      console.log('🔄 Rechargement des données de la pesée:', weighingId);
      
      const response = await weighingService.getPesée(parseInt(weighingId));

      if (response.success && response.data) {
        dispatch({ type: 'SET_CURRENT_WEIGHING', payload: response.data });
        console.log('✅ Données de la pesée rechargées avec succès');
      } else {
        throw new Error(response.message || 'Erreur lors du rechargement');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du rechargement de la pesée';
      console.error('❌ Erreur lors du rechargement:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Action pour effacer la pesée actuelle
  const clearWeighing = useCallback(() => {
    dispatch({ type: 'CLEAR_WEIGHING' });
  }, []);

  // Action pour définir l'état de chargement
  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  // Action pour définir l'erreur
  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const contextValue: WeighingContextType = {
    state,
    setCurrentWeighing,
    updateWeighing,
    refreshWeighing,
    clearWeighing,
    setLoading,
    setError,
  };

  return (
    <WeighingContext.Provider value={contextValue}>
      {children}
    </WeighingContext.Provider>
  );
};

// Hook pour utiliser le contexte
export const useWeighing = (): WeighingContextType => {
  const context = useContext(WeighingContext);
  if (context === undefined) {
    throw new Error('useWeighing must be used within a WeighingProvider');
  }
  return context;
};

// Hook pour accéder uniquement à la pesée actuelle
export const useCurrentWeighing = (): Pesée | null => {
  const { state } = useWeighing();
  return state.currentWeighing;
};

// Hook pour accéder à l'état de chargement
export const useWeighingLoading = (): boolean => {
  const { state } = useWeighing();
  return state.isLoading;
};

// Hook pour accéder aux erreurs
export const useWeighingError = (): string | null => {
  const { state } = useWeighing();
  return state.error;
};

export default WeighingContext;
